import React, { useEffect, useMemo, useState } from 'react';
import { Box, Paper, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { ActivityDay } from '@/types/client-dashboard';
import { getPmClientDashboard } from '@services/client-dashboard.service';

// Активність клієнта по днях (календарі, виміри, харчування, тренування, файли) за 30/60/90 днів.
// Без даних не рендериться.

const CALENDAR_COLORS = ['#1565c0', '#2e7d32', '#6a1b9a', '#00695c', '#c62828', '#4527a0', '#e65100', '#f9a825'];

export const PmClientActivityWidget: React.FC<{ clientId: number }> = ({ clientId }) => {
  const [activityDays, setActivityDays] = useState<ActivityDay[]>([]);
  const [activityDaysCount, setActivityDaysCount] = useState<30 | 60 | 90>(30);

  useEffect(() => {
    getPmClientDashboard(clientId, activityDaysCount)
      .then((data) => setActivityDays(data.activity.days))
      .catch(() => {});
  }, [clientId, activityDaysCount]);

  // Build chart data: flatten calendars into keyed fields per day
  const { chartData, calendarList } = useMemo(() => {
    // Collect all unique calendars across all days (preserve order of first appearance)
    const calMap = new Map<number, string>(); // calendarId -> calendarName
    activityDays.forEach((day) => {
      day.calendars.forEach((c) => {
        if (!calMap.has(c.calendarId)) calMap.set(c.calendarId, c.calendarName);
      });
    });

    const calendarList = Array.from(calMap.entries()).map(([id, name], idx) => ({
      id,
      name,
      key: `cal_${id}`,
      color: CALENDAR_COLORS[idx % CALENDAR_COLORS.length],
    }));

    const chartData = activityDays.map((day) => {
      const row: Record<string, any> = {
        label: day.date.slice(5),
        measurements: day.measurements,
        files: day.files,
        foods: day.foods,
        workouts: day.workouts,
      };
      calendarList.forEach(({ id, key }) => {
        const found = day.calendars.find((c) => c.calendarId === id);
        row[key] = found?.events ?? 0;
      });
      return row;
    });

    return { chartData, calendarList };
  }, [activityDays]);

  if (chartData.length === 0) return null;

  return (
    <Paper sx={{ p: 2, mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Typography variant="subtitle1" fontWeight={600} display="flex" alignItems="center" gap={1}>
          <PersonIcon fontSize="small" color="primary" /> Активність за {activityDaysCount} днів
        </Typography>
        <ToggleButtonGroup
          value={activityDaysCount}
          exclusive
          size="small"
          onChange={(_, v) => v && setActivityDaysCount(v)}
          sx={{ height: 28 }}
        >
          <ToggleButton value={30} sx={{ px: 1.5, fontSize: '0.75rem' }}>30д</ToggleButton>
          <ToggleButton value={60} sx={{ px: 1.5, fontSize: '0.75rem' }}>60д</ToggleButton>
          <ToggleButton value={90} sx={{ px: 1.5, fontSize: '0.75rem' }}>90д</ToggleButton>
        </ToggleButtonGroup>
      </Box>
      <ResponsiveContainer width="100%" height={200}>
        <BarChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 4 }} barCategoryGap="20%">
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="label" tick={{ fontSize: 11 }} interval={4} tickLine={false} />
          <YAxis allowDecimals={false} width={24} tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
          <Tooltip
            cursor={{ fill: 'rgba(0,0,0,0.05)' }}
            formatter={(value: number, name: string) => {
              const calEntry = calendarList.find((c) => c.key === name);
              const staticLabels: Record<string, string> = {
                measurements: 'Виміри', files: 'Файли', foods: 'Харчування', workouts: 'Тренування',
              };
              return [value, calEntry ? calEntry.name : (staticLabels[name] ?? name)];
            }}
            labelFormatter={(label) => `Дата: ${label}`}
          />
          <Legend
            formatter={(value) => {
              const calEntry = calendarList.find((c) => c.key === value);
              const staticLabels: Record<string, string> = {
                measurements: 'Виміри', files: 'Файли', foods: 'Харчування', workouts: 'Тренування',
              };
              return calEntry ? calEntry.name : (staticLabels[value] ?? value);
            }}
            wrapperStyle={{ fontSize: 12 }}
          />
          {/* Dynamic bars — one per calendar */}
          {calendarList.map((cal, idx) => (
            <Bar
              key={cal.key}
              dataKey={cal.key}
              stackId="a"
              fill={cal.color}
              radius={idx === calendarList.length - 1 && chartData.every(d => !d.measurements && !d.foods && !d.workouts && !d.files) ? [4, 4, 0, 0] : undefined}
            />
          ))}
          <Bar dataKey="measurements" stackId="a" fill="#388e3c" />
          <Bar dataKey="foods"        stackId="a" fill="#d32f2f" />
          <Bar dataKey="workouts"     stackId="a" fill="#7b1fa2" />
          <Bar dataKey="files"        stackId="a" fill="#f57c00" radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </Paper>
  );
};
